import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { certifications, homeImages, homeServices } from '../data/homeLayout';
import { HomeLeadForm } from '../components/HomeLeadForm';
import { HeroStats } from '../components/HeroStats';
import { FacebookFeed } from '../components/FacebookFeed';

function ImageBackground({ image, className = '' }) {
  return (
    <div className={`absolute inset-0 ${className}`} aria-hidden="true">
      <img src={image.src} srcSet={image.srcSet} sizes="100vw" alt="" className="h-full w-full object-cover" />
    </div>
  );
}

export function HomePage() {
  return (
    <>
      <Helmet>
        <title>Energi montør i Holsted, Vejen og Esbjerg - Få et tilbud i dag</title>
        <meta name="description" content="Leder du efter en energi montør i Holsted? +40 års erfaring som certificerede montører i Vejen og Esbjerg. Kontakt os i dag" />
        <meta property="og:title" content="Energi montør i Holsted, Vejen og Esbjerg - Få et tilbud i dag" />
        <meta property="og:description" content="Leder du efter en energi montør i Holsted? +40 års erfaring som certificerede montører i Vejen og Esbjerg. Kontakt os i dag" />
      </Helmet>

      <section className="relative isolate min-h-[780px] overflow-hidden text-white lg:min-h-[940px]">
        <ImageBackground image={homeImages.hero} />
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative mx-auto flex min-h-[780px] max-w-[1640px] items-end px-5 pb-20 sm:px-8 sm:pb-24 lg:min-h-[940px] lg:px-12 lg:pb-32">
          <div className="max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-white sm:text-sm">JNO Montage</p>
            <h1 className="mt-5 max-w-4xl text-4xl font-medium leading-[1.09] tracking-tight sm:text-5xl lg:text-[68px]">Vi leverer høj kvalitet<br />og samler på glade kunder</h1>
            <Link to="/kontakt" className="mt-10 inline-flex items-center gap-5 rounded-xl bg-[#206FEA] px-7 py-4 text-sm font-semibold text-white transition hover:bg-[#206FEA]/85">Kontakt os <i className="fa-light fa-arrow-right text-base" aria-hidden="true" /></Link>
          </div>
        </div>
      </section>

      <section className="relative bg-white py-18 lg:py-28">
        <div className="mx-auto grid max-w-[1640px] grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-[1.32fr_.82fr] lg:gap-24 lg:px-12">
          <div className="max-w-3xl lg:pt-10">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#0B0E14]">Høj kvalitet</p>
            <h2 className="mt-5 text-3xl font-semibold leading-tight tracking-tight text-[#0B0E14] sm:text-4xl lg:text-[46px]">Mere end 40 års erfaring med installation og service</h2>
            <div className="mt-8 space-y-5 text-[15px] leading-8 text-slate-600 sm:text-base">
              <p>Står du og skal bruge en montør til installation eller opgradering af dit varmeanlæg? Som energi montør hos JNO Montage arbejder vi med både montering, udskiftning og løbende vedligehold, så du får en samlet løsning fra start til drift.</p>
              <p>Vi arbejder med installation af pillefyr, luft til luft varmepumpe, luft til vand varmepumpe og solceller. Vi dimensionerer og installerer komplette energisystemer, så løsningen passer til bygningens behov.</p>
              <p>Vi holder til i Holsted og udfører opgaver i Vejen, Esbjerg og nærliggende områder i Syd- og Vestjylland for både private boligejere og erhverv.</p>
            </div>
            <Link to="/om-os" className="mt-8 inline-flex items-center gap-3 text-sm font-semibold text-[#206FEA] transition hover:text-[#0B0E14]">Læs mere om os <i className="fa-light fa-arrow-right" aria-hidden="true" /></Link>
          </div>
          <div className="relative lg:-mt-40">
            <HomeLeadForm />
          </div>
        </div>
      </section>

      <section className="bg-[#E6EBEF] py-10 sm:py-14">
        <div className="mx-auto max-w-[1640px] px-5 sm:px-8 lg:px-12">
          <HeroStats />
        </div>
      </section>

      <section className="relative isolate overflow-hidden py-20 text-white lg:py-28">
        <ImageBackground image={homeImages.serviceBand} />
        <div className="absolute inset-0 bg-[#0B0E14]/80" />
        <div className="relative mx-auto max-w-[1640px] px-5 sm:px-8 lg:px-12">
          <div className="max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-white">JNO Montage</p>
            <h2 className="mt-5 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-[46px]">Vi leverer høj kvalitet</h2>
            <div className="mt-6 max-w-3xl space-y-4 font-serif text-[15px] leading-7 text-white/85">
              <p>Vi leverer energiløsninger, hvor god rådgivning, korrekt dimensionering og omhyggelig montage hænger sammen.</p>
              <p>Uanset om du skal have et nyt varmeanlæg, service på din varmepumpe eller hjælp til energioptimering, får du en løsning, der er tilpasset dit behov.</p>
            </div>
          </div>
          <div className="mt-14 grid grid-cols-1 border border-white/25 sm:grid-cols-2 lg:grid-cols-3">
            {homeServices.map((service) => (
              <Link key={service.href} to={service.href} className="group flex min-h-64 flex-col items-center justify-center border border-white/20 px-6 py-9 text-center transition hover:bg-white/10 lg:min-h-72">
                <i className={`fa-light ${service.icon} text-5xl text-white`} aria-hidden="true" />
                <h3 className="mt-7 text-lg font-medium uppercase tracking-[0.1em]">{service.title}</h3>
                <span className="mt-4 font-serif text-sm text-white/80">Læs mere <i className="fa-light fa-arrow-right ml-1 transition-transform group-hover:translate-x-1" aria-hidden="true" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-[1640px] grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-18 lg:px-12">
          <div className="group relative overflow-hidden shadow-[0_12px_28px_rgba(0,0,0,0.18)]">
            <img src={homeImages.explainer.src} srcSet={homeImages.explainer.srcSet} sizes="(min-width: 1024px) 48vw, 100vw" alt={homeImages.explainer.alt} className="aspect-video w-full object-cover" />
            <div className="absolute inset-0 grid place-items-center bg-black/20" />
          </div>
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B0E14]">JNO Montage · Naturvarme</p>
            <h2 className="mt-5 text-3xl font-semibold tracking-tight text-[#0B0E14] sm:text-4xl lg:text-[46px]">Hvad er en varmepumpe?</h2>
            <div className="mt-7 space-y-5 text-[15px] leading-8 text-slate-600 sm:text-base">
              <p>En varmepumpe udnytter energien i luften eller jorden og omdanner den til varme i boligen. Det kan give en stabil, effektiv og mere energivenlig opvarmning.</p>
              <p>Den rette løsning afhænger af bygningen, dit nuværende varmeanlæg og det forventede forbrug. Derfor starter vi altid med en faglig gennemgang.</p>
              <p>Vi hjælper med rådgivning, dimensionering, installation og efterfølgende service, så du får én samlet løsning.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative isolate overflow-hidden py-22 text-white lg:py-30">
        <ImageBackground image={homeImages.credentials} />
        <div className="absolute inset-0 bg-[#0B0E14]/80" />
        <div className="relative mx-auto max-w-[1640px] px-5 text-center sm:px-8 lg:px-12">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-white">Hvorfor vælge os</p>
          <h2 className="mx-auto mt-5 max-w-5xl text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-[46px]">Hos JNO Montage får du en løsning, som sikrer stabil drift og kvalitet i hele processen</h2>
          <div className="mx-auto mt-16 grid max-w-5xl grid-cols-1 gap-12 sm:grid-cols-3 sm:gap-8">
            {certifications.map((credential) => <div key={credential.label} className="flex flex-col items-center"><i className={`fa-light ${credential.icon} text-5xl`} aria-hidden="true" /><h3 className="mt-6 text-base font-medium uppercase tracking-[0.11em]">{credential.label}</h3></div>)}
          </div>
        </div>
      </section>

      <FacebookFeed />
    </>
  );
}
